<template>
  <lf-dropdown
    :name
    :value="model"
    :options="priorityLevels"
    menu-width="w-28"
    :disabled
    :teleport-options
    :custom-z-index
    @change="handleChange"
  >
    <template #toggle-button="{ toggle, value, menuOpen }">
      <div
        class="toggle-button flex items-center justify-center space-x-1 rounded-2xl border bg-white px-3 h-7 uppercase"
        :class="
          disabled
            ? 'pointer-events-none items-center'
            : 'cursor-pointer justify-between'
        "
        data-cy="priority-toggle"
        @click="toggle"
      >
        <div class="flex items-center space-x-1">
          <icon-base
            v-if="iconConfig"
            :class="iconConfig.class"
            :icon="iconConfig.icon"
            :view-box="iconConfig.viewBox"
          />
          <span v-if="showValue" class="font-semibold capitalize">
            {{ value }}
          </span>
        </div>
        <icon-base
          v-if="!disabled"
          height="6"
          width="10"
          :icon="IconDropdownArrow"
          :class="[
            'transform transition-transform',
            menuOpen ? 'rotate-180' : 'rotate-0',
          ]"
        />
      </div>
    </template>
  </lf-dropdown>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";

import { prioritiesIconsMap } from "@/helpers/constants/priorities";

import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

import type { DropdownTeleportOptions } from "@/components/ui/inputs/LfDropdown.vue";
import type { Priority } from "@/models/applications";
import type { IOptions } from "@/models/options";

const {
  name = "priority_level",
  disabled = false,
  showValue = true,
  teleportOptions,
  customZIndex,
  allowNone = true,
} = defineProps<{
  name?: string;
  disabled?: boolean;
  showValue?: boolean;
  teleportOptions?: Partial<DropdownTeleportOptions>;
  customZIndex?: string;
  allowNone?: boolean;
}>();

const model = defineModel<Priority>({ default: "none" });

const { getters } = useStore();

const options = computed<IOptions>(() => getters["options/all"]);

const priorityLevels = computed(() => {
  const levels = options.value.priority_levels;

  if (!levels) {
    return {};
  }

  if (allowNone) {
    return levels;
  }

  // Removes `none` from the object without mutating
  // `options.value.priority_levels`, but leaves `none` unused.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { none, ...rest } = levels;

  return rest;
});

const iconConfig = computed(() => {
  if (!model.value) {
    return null;
  }

  return prioritiesIconsMap[model.value];
});

const handleChange = (value: string | number) => {
  if (disabled) {
    return;
  }

  model.value = value as Priority;
};
</script>
